import type { Apartment, Condominium, Key, KeyHistory } from '@/types/api';
import { formatGeocodeAddressQuery } from '@/utils/addressNormalize';

/** Minimal shape for list vs official name (avoids importing full Condominium in some call sites). */
export type CondominiumListDisplayInput = { name?: string | null; listName?: string | null };

/** Strip leading "As Oy" (case-insensitive, flexible spaces). If nothing left, return trimmed official name. */
export function suggestListNameFromOfficialName(name: string): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '';
  const withoutPrefix = trimmed.replace(/^as\s+oy\s+/i, '').trim();
  return withoutPrefix.length > 0 ? withoutPrefix : trimmed;
}

/** Label shown in lists, breadcrumbs, and tables. */
export function getCondominiumListDisplayName(c: CondominiumListDisplayInput | null | undefined): string {
  if (!c) return '';
  const list = (c.listName ?? '').trim();
  if (list.length > 0) return list;
  return (c.name ?? '').trim();
}

// Types for holder entities
type HolderEntity = {
  firstName?: string;
  lastName?: string;
  fullName?: string;
};

/** ExternalUser uses string `company`; User has relation object `company` — only append for string. */
function holderCompanySuffix(holder: object): string {
  if (!('company' in holder)) return '';
  const c = (holder as { company?: unknown }).company;
  if (typeof c !== 'string' || !c.trim()) return '';
  return ` (${c.trim()})`;
}

/**
 * Centralized logic for displaying holder names consistently
 */
export const getHolderDisplay = (holder?: HolderEntity | null): string => {
  if (!holder) return '-';

  const suffix = holderCompanySuffix(holder as object);

  if ('fullName' in holder && holder.fullName) {
    return `${holder.fullName}${suffix}`;
  }

  let name: string;
  if (holder.firstName && holder.lastName) {
    name = `${holder.firstName} ${holder.lastName}`;
  } else if (holder.firstName) {
    name = holder.firstName;
  } else if (holder.lastName) {
    name = holder.lastName;
  } else {
    return '-';
  }

  return `${name}${suffix}`;
};

/**
 * Get location display from key history or key entity
 */
export const getLocationDisplay = (entity: KeyHistory | Key): string => {
  const key = 'key' in entity ? entity.key : entity;

  if (key?.apartment) {
    // Match apartment name/address logic used in apartment tables:
    // prefer explicit name, then full address, then street.
    return key.apartment.name || key.apartment.fullAddress || key.apartment.addressStreet || '';
  }

  if (key?.condominium) {
    return getCondominiumListDisplayName(key.condominium) || key.condominium.fullAddress || '';
  }

  return '-';
};

/**
 * Get key holder from key history
 */
export const getKeyHistoryHolder = (history: KeyHistory): HolderEntity | null => {
  return history.resident || history.user || history.landlord || history.other || null;
};

/** Physical returner on a return event, when stored on the history row. */
export const getKeyHistoryPhysicalReturner = (history: KeyHistory): HolderEntity | null => {
  return (
    history.returnedByResident ||
    history.returnedByUser ||
    history.returnedByLandlord ||
    history.returnedByOther ||
    null
  );
};

/**
 * Get current key holder from key entity
 */
export const getCurrentKeyHolder = (key: Key): HolderEntity | null => {
  return key.currentHolder || key.currentHolderInternal || key.currentHolderLandlord || key.currentHolderOther || null;
};

/** Slice of return-key forms: who physically returned (resident / user / landlord / external). */
export type ReturnKeyReturnedByFormSlice = {
  returnedByType: 'resident' | 'user' | 'landlord' | 'other';
  residentId: string;
  userId: string;
  landlordId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
};

const emptyReturnedByFormSlice = (): ReturnKeyReturnedByFormSlice => ({
  returnedByType: 'resident',
  residentId: '',
  userId: '',
  landlordId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
});

function currentHolderIdentitySignature(key: Key): string {
  const r = key.currentHolder;
  if (r?.id) return `resident:${r.id}`;
  const u = key.currentHolderInternal;
  if (u?.id) return `user:${u.id}`;
  const l = key.currentHolderLandlord;
  if (l?.id) return `landlord:${l.id}`;
  const o = key.currentHolderOther;
  if (o && ((o.firstName && o.firstName.trim()) || (o.lastName && o.lastName.trim()))) {
    const fn = (o.firstName ?? '').trim();
    const ln = (o.lastName ?? '').trim();
    return `other:${fn}|${ln}|${o.email ?? ''}|${o.phone ?? ''}|${o.company ?? ''}`;
  }
  return 'none';
}

function keyToReturnedByFormSlice(key: Key, rentalBrokerageEnabled: boolean): ReturnKeyReturnedByFormSlice {
  if (key.currentHolder?.id) {
    return {
      ...emptyReturnedByFormSlice(),
      returnedByType: 'resident',
      residentId: String(key.currentHolder.id),
    };
  }
  if (key.currentHolderInternal?.id) {
    return {
      ...emptyReturnedByFormSlice(),
      returnedByType: 'user',
      userId: String(key.currentHolderInternal.id),
    };
  }
  if (key.currentHolderLandlord?.id) {
    if (!rentalBrokerageEnabled) return emptyReturnedByFormSlice();
    return {
      ...emptyReturnedByFormSlice(),
      returnedByType: 'landlord',
      landlordId: String(key.currentHolderLandlord.id),
    };
  }
  const o = key.currentHolderOther;
  if (o && ((o.firstName && o.firstName.trim()) || (o.lastName && o.lastName.trim()))) {
    return {
      ...emptyReturnedByFormSlice(),
      returnedByType: 'other',
      firstName: o.firstName ?? '',
      lastName: o.lastName ?? '',
      email: o.email ?? '',
      phone: o.phone ?? '',
      company: o.company ?? '',
    };
  }
  return emptyReturnedByFormSlice();
}

/**
 * Default "who returned" fields when every key in the batch is held by the same person.
 * If holders differ or are missing, falls back to resident with empty selection (previous default UX).
 */
export function getDefaultReturnedByPartialFromKeys(
  keys: Key[],
  rentalBrokerageEnabled: boolean,
): ReturnKeyReturnedByFormSlice {
  if (!keys.length) return emptyReturnedByFormSlice();
  const sig0 = currentHolderIdentitySignature(keys[0]);
  if (sig0 === 'none') return emptyReturnedByFormSlice();
  for (let i = 1; i < keys.length; i++) {
    if (currentHolderIdentitySignature(keys[i]) !== sig0) return emptyReturnedByFormSlice();
  }
  return keyToReturnedByFormSlice(keys[0], rentalBrokerageEnabled);
}

/**
 * Format key name/identifier
 */
export const getKeyName = (key: Key): string => {
  const name = key.name?.trim();
  const stamp = key.stamp?.trim();
  if (name && stamp) {
    return `${name} (${stamp})`;
  }
  if (name) {
    return name;
  }
  if (stamp) {
    return stamp;
  }
  return String(key.id);
};

function joinSignatureParts(parts: string[]): string {
  return parts.filter((p) => p.length > 0).join(' — ');
}

function addressLineFromStreetPostalCity(street?: string | null, postal?: string | null, city?: string | null): string {
  return [street, postal, city]
    .map((x) => (x ?? '').trim())
    .filter((x) => x.length > 0)
    .join(', ');
}

function apartmentAddressLine(apt: Apartment): string {
  const full = apt.fullAddress?.trim();
  if (full) return full;
  return addressLineFromStreetPostalCity(apt.addressStreet, apt.addressPostalCode, apt.addressCity);
}

function condominiumAddressLine(c: Condominium): string {
  const full = c.fullAddress?.trim();
  if (full) return full;
  return addressLineFromStreetPostalCity(c.addressStreet, c.addressPostalCode, c.addressCity);
}

/**
 * One-line summary for remote signing: key id, then apartment name / address / condo,
 * or condominium name / address for building-level keys.
 */
export function getKeyRemoteSignatureLine(key: Key): string {
  const keyLabel = getKeyName(key);
  const apt = key.apartment;
  const isApartmentKey = typeof key.apartmentId === 'number' && key.apartmentId > 0 && apt != null;

  if (isApartmentKey) {
    const addressTrimmed = apartmentAddressLine(apt).trim();
    const fromName = (apt.name ?? '').trim();
    const fromNumber = (apt.number ?? '').trim();
    const fromDisplay = (apt.displayName ?? '').trim();

    /** Avoid listing the same address twice when displayName is just the formatted address. */
    let aptNamePart = '';
    if (fromName && fromName !== addressTrimmed) {
      aptNamePart = fromName;
    } else if (fromNumber && fromNumber !== addressTrimmed) {
      aptNamePart = fromNumber;
    } else if (fromDisplay && fromDisplay !== addressTrimmed) {
      aptNamePart = fromDisplay;
    }

    const condo = apt.condominium ?? key.condominium;
    const condoName = condo ? getCondominiumListDisplayName(condo) : '';
    return joinSignatureParts([keyLabel, aptNamePart, addressTrimmed, condoName]);
  }

  const c = key.condominium;
  if (c) {
    const addressTrimmed = condominiumAddressLine(c).trim();
    const condoName = getCondominiumListDisplayName(c);
    if (condoName && condoName === addressTrimmed) {
      return joinSignatureParts([keyLabel, addressTrimmed]);
    }
    return joinSignatureParts([keyLabel, condoName, addressTrimmed]);
  }

  return keyLabel;
}

/**
 * Get apartment address with fallbacks
 */
export const getApartmentAddress = (apartment?: { fullAddress?: string; addressStreet?: string } | null): string => {
  if (!apartment) return '-';
  return apartment.fullAddress || apartment.addressStreet || '-';
};

/** Minimal shape for building an apartment select option label (e.g. rental brokerage, tracked assets). */
export type RentalApartmentOptionInput = {
  number?: string | number | null;
  addressStreet?: string | null;
  addressPostalCode?: string | null;
  addressCity?: string | null;
};

/** "12 · Street 1, 00100 Helsinki" style label used across apartment select options. */
export function formatRentalApartmentOptionLabel(apartment: RentalApartmentOptionInput): string {
  const street = [apartment.addressStreet, apartment.addressPostalCode, apartment.addressCity]
    .filter(Boolean)
    .join(', ');
  return apartment.number ? `${apartment.number} · ${street}` : street || '';
}

/**
 * Get full address from address components
 */
export const formatFullAddress = (street?: string, postalCode?: string, city?: string): string => {
  const query = formatGeocodeAddressQuery(street, postalCode, city);
  if (!street || !postalCode || !city || !query) return '-';
  return query;
};

/**
 * Compare if current and original holders are the same person
 * Uses type and ID for accurate comparison
 */
export const areHoldersSame = (key: Key): boolean => {
  const currentLandlord = key.currentHolderLandlord;
  const originalLandlord = key.originalHolderLandlord;
  if (currentLandlord || originalLandlord) {
    if (currentLandlord && originalLandlord) {
      return currentLandlord.id === originalLandlord.id;
    }
    return false;
  }

  // Get the actual holder objects, not just display names
  const currentHolder = key.currentHolder || key.currentHolderInternal || key.currentHolderOther;
  const originalHolder = key.originalHolder || key.originalHolderInternal || key.originalHolderOther;

  if (!currentHolder || !originalHolder) return false;

  // Compare by type and ID
  // For residents and users, compare by ID
  if (currentHolder.id && originalHolder.id) {
    return currentHolder.id === originalHolder.id;
  }

  // For external persons (currentHolderOther/originalHolderOther), compare by name and email
  if (key.currentHolderOther && key.originalHolderOther) {
    return (
      key.currentHolderOther.firstName === key.originalHolderOther.firstName &&
      key.currentHolderOther.lastName === key.originalHolderOther.lastName &&
      key.currentHolderOther.email === key.originalHolderOther.email &&
      (key.currentHolderOther.company ?? '') === (key.originalHolderOther.company ?? '')
    );
  }

  return false;
};

/**
 * Capitalize the first letter of a string
 * Preserves the rest of the string as-is
 */
export const capitalizeFirstLetter = (str: string): string => {
  if (!str || str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};
